import { createContext, useContext, useMemo, type ReactNode } from "react";
import { Container, Heading, Text, VStack } from "@chakra-ui/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { APIPortal } from "models/types/api-listing";
import { sessionModel, type SessionData } from "contexts/session";
import { usePortalPath } from "hooks/use-portal-path";
import { toApiRole, type Role } from "types/roles";
import { getAuthToken, isTokenExpired } from "utils/auth";

export type AuthContextType = {
  user?: SessionData;
  isLandlord: boolean;
  isTenant: boolean;
  isVendor: boolean;
  isAgent: boolean;
  isAgentManager: boolean;
  hasRole: (role: Role) => boolean;
  region?: APIPortal["region"]["id"];
};

const AuthContext = createContext<AuthContextType>({
  isLandlord: false,
  isTenant: false,
  isVendor: false,
  isAgent: false,
  isAgentManager: false,
  hasRole: () => false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

function Unauthorised() {
  return (
    <Container maxW={"2xl"} pt={"32"}>
      <VStack spacing={"4"} w={"100%"}>
        <Heading>Unauthorised</Heading>
        <Text textAlign={"center"}>
          You are not authorised to access this page.
          <br />
          If you accessed this page from an email link, it may have expired.
          Please reach out to your agent for a new link.
        </Text>
      </VStack>
    </Container>
  );
}

function AuthProviderInner({ children }: { children: ReactNode }) {
  const { data: sessionData } = useSuspenseQuery(
    sessionModel.queries.read(getAuthToken()),
  );

  const contextValue = useMemo(() => {
    const hasRole = (role: Role) =>
      sessionData?.roles.includes(toApiRole(role)) || false;

    return {
      user: sessionData,
      hasRole,
      isVendor: hasRole("vendor"),
      isTenant: hasRole("tenant"),
      isLandlord: hasRole("landlord"),
      isAgent: hasRole("agent"),
      isAgentManager: hasRole("agent_manager"),
      region: sessionData?.region?.id,
    };
  }, [sessionData]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLandlordOrTenantPath } = usePortalPath();

  if (isLandlordOrTenantPath && isTokenExpired()) {
    return children;
  }

  return <AuthProviderInner>{children}</AuthProviderInner>;
}

export function RequireAuthRole({
  anyOf,
  children,
}: {
  anyOf?: Role[];
  children: ReactNode;
}) {
  const auth = useAuth();
  const token = getAuthToken();

  if (!token && import.meta.env.DEV) {
    return <Unauthorised />;
  }

  if (anyOf && !anyOf.some((role) => auth.hasRole(role))) {
    return <Unauthorised />;
  }

  return children;
}
